export type DueDateGroupMap = Record<string, number[]>

export type DueDatePayload = {
  holder_only: boolean
  groups: DueDateGroupMap
}

export const DEFAULT_DUE_DATE_GROUPS: DueDateGroupMap = {
  '4': [5, 7, 10, 12, 15, 17],
  '5': [5, 7, 10, 12, 15, 17],
  '6': [5, 7, 10, 12, 15, 17],
  '7': [7, 10, 12, 15, 17, 20],
  '8': [10, 12, 15, 18, 20, 22],
  '9': [10, 12, 15, 18, 20, 22],
  '10': [10, 12, 15, 17, 20, 25],
  '11': [10, 12, 15, 17, 20, 25],
  '12': [10, 12, 15, 17, 20, 25],
  '13': [13, 16, 18, 20, 22, 25],
  '14': [13, 16, 18, 20, 22, 25],
  '15': [13, 15, 20, 23, 25, 27],
  '16': [13, 15, 20, 23, 25, 27],
  '17': [15, 19, 21, 23, 25, 27],
  '18': [15, 19, 21, 23, 25, 27],
  '19': [15, 19, 21, 23, 25, 27],
  '20': [15, 19, 21, 23, 25, 27],
  '21': [15, 19, 21, 23, 25, 27],
  '22': [15, 19, 21, 23, 25, 27],
  '23': [15, 19, 21, 23, 25, 27],
  '50': [5, 7, 10, 12, 15, 17],
  '60': [7, 10, 12, 15, 17, 20],
  '99': [15, 19, 21, 23, 25, 27],
}

export const DEFAULT_DUE_DATE_PAYLOAD: DueDatePayload = {
  holder_only: true,
  groups: DEFAULT_DUE_DATE_GROUPS,
}

export function normalizeGroupCode(value: string) {
  const trimmed = value.trim()
  if (!/^\d+$/.test(trimmed)) return ''
  const numeric = Number.parseInt(trimmed, 10)
  return Number.isSafeInteger(numeric) && numeric >= 0 ? String(numeric) : ''
}

export function normalizeDays(value: unknown): number[] {
  if (!Array.isArray(value)) return []
  return Array.from(new Set(value.map(Number).filter((day) => Number.isInteger(day) && day >= 1 && day <= 31))).sort((a, b) => a - b)
}

export function normalizeDueDatePayload(value: unknown): DueDatePayload {
  if (!value || typeof value !== 'object') return DEFAULT_DUE_DATE_PAYLOAD
  const raw = value as Record<string, unknown>
  const rawGroups = raw.groups && typeof raw.groups === 'object' ? raw.groups as Record<string, unknown> : {}
  const groups: DueDateGroupMap = {}
  Object.entries(rawGroups).forEach(([group, days]) => {
    const code = normalizeGroupCode(group)
    const normalized = normalizeDays(days)
    if (code && normalized.length > 0) groups[code] = normalized
  })
  return {
    holder_only: raw.holder_only !== false,
    groups: Object.keys(groups).length > 0 ? groups : DEFAULT_DUE_DATE_GROUPS,
  }
}

export function parseDaysText(value: string): number[] {
  if (!value.trim()) return []
  const numbers = value.split(/[;,\s]+/).filter(Boolean).map((part) => Number(part)).filter(Number.isInteger)
  return Array.from(new Set(numbers)).sort((a, b) => a - b)
}

export function validateDueDateGroups(groups: DueDateGroupMap): string[] {
  const errors: string[] = []
  const entries = Object.entries(groups)
  if (entries.length === 0) errors.push('Cadastre pelo menos um grupo de leitura.')
  for (const [group, days] of entries) {
    if (!normalizeGroupCode(group)) errors.push(`Grupo inválido: ${group || 'em branco'}.`)
    if (!Array.isArray(days) || days.length === 0) errors.push(`O grupo ${group} precisa ter pelo menos um dia permitido.`)
    if (days.some((day) => !Number.isInteger(day) || day < 1 || day > 31)) errors.push(`O grupo ${group} possui dia fora do intervalo de 1 a 31.`)
    if (new Set(days).size !== days.length) errors.push(`O grupo ${group} possui dias duplicados.`)
  }
  return errors
}

export function allowedDaysForGroup(payload: DueDatePayload, groupInput: string) {
  const group = normalizeGroupCode(groupInput)
  return group ? payload.groups[group] ?? null : null
}

export function formatDayList(days: number[]) {
  return days.map((day) => String(day).padStart(2, '0')).join(' · ')
}

export function buildMonthCells(year: number, monthIndex: number): Array<number | null> {
  const firstWeekday = new Date(year, monthIndex, 1).getDay()
  const daysInMonth = new Date(year, monthIndex + 1, 0).getDate()
  const cells: Array<number | null> = Array.from({ length: firstWeekday }, () => null)
  for (let day = 1; day <= daysInMonth; day += 1) cells.push(day)
  while (cells.length % 7 !== 0) cells.push(null)
  return cells
}
